/**
 * NewItemModal — Shared F2/F3 New Item modal for all procurement screens
 * Matches HTML prototype exactly:
 *   Quick entry → Item Image → Classification → Price → Item Details → Others
 *   + Markup/Markdown badges + Inventory type row + Legend
 *
 * Usage (same in PI, GRN, GRC screens):
 *   import NewItemModal from '@/components/procurement/NewItemModal';
 *   {showNewItem && <NewItemModal onSave={row=>...} onClose={()=>setShowNewItem(false)}/>}
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import GetInfoPopup from '../GetInfoPopup';
import ImageManagerModal from './ImageManagerModal';
import UnitIdentifierModal from './UnitIdentifierModal';
import { loadImages } from '../../lib/imageStorage';

// ─── Autofill data ──────────────────────────────────────────────────────────
const AUTOFILL = {
  Division: ['Textiles','Electronics','Footwear','Cosmetics','Jewellery','Pharma','Grocery','Home Decor','Toys','Stationery'],
  Dept:     ['Mens','Womens','Kids','Unisex','Infants','Accessories','Home'],
  Category: ['Shirts','Trousers','Sarees','T-Shirts','Jackets','Footwear','Bags','Watches','Kurtas','Dresses'],
  Subcatg:  ['P Sarees','Casual','Formal','Sports','Ethnic'],
  Brand:    ['Nike','Adidas','Puma','H&M','Zara','Levis','Arrow'],
  Style:    ['Regular','Slim Fit','Loose Fit','Classic','Relaxed'],
  Size:     ['XS','S','M','L','XL','XXL','28','30','32','34','36','38','40'],
  Color:    ['Red','Blue','Green','Black','White','Grey','Yellow','Pink','Orange'],
  Design:   ['Solid','Striped','Printed','Checks','Embroidered'],
  Cref:     [],
};

const CLS_LKP = {
  division:'5.5.8/1', dept:'5.5.11/1', category:'5.5.2/1',
  subcatg:'5.5.12/1', brand:'5.5.3/1',  style:'5.5.10/1',
  size:'5.5.9/1',     color:'5.5.4/1',  design:'5.5.5/1',
};
function parseSI(str){const[screenId,controlId]=str.split('/');return{screenId,controlId};}

// ─── AutoInput field ────────────────────────────────────────────────────────
function AutoInput({ id, value, dataKey, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState(value || '');
  const ref             = useRef(null);
  const list = (AUTOFILL[dataKey] || []).filter(x => !q || x.toLowerCase().includes(q.toLowerCase())).slice(0, 10);
  useEffect(() => { setQ(value || ''); }, [value]);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div className="pi2-autoinput" ref={ref}>
      <input id={id} value={q} autoComplete="off"
        onChange={e => { setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} />
      <button type="button" className="pi2-lkpbtn" onClick={() => setOpen(o => !o)}>🔍</button>
      {open && list.length > 0 && (
        <div className="pi2-auto-dd">
          {list.map(x => <button key={x} type="button" onClick={() => { setQ(x); onChange(x); setOpen(false); }}>{x}</button>)}
        </div>
      )}
    </div>
  );
}

// ─── Main Modal ─────────────────────────────────────────────────────────────
export default function NewItemModal({ onSave, onClose, initialValues = null, sourceScreen = 'Procurement › Goods Receipts & Barcode' }) {
  const isEdit = !!initialValues; // F3 edit mode when initialValues provided
  const [gstType, setGstType] = useState(initialValues?.gstType || 'excl');
  const [invType, setInvType] = useState('inventory');
  const [showImgMgr, setShowImgMgr] = useState(false);
  const [showImeiMgr, setShowImeiMgr] = useState(false);
  const [giftVoucher, setGiftVoucher] = useState(false);
  const [reverseMrp, setReverseMrp] = useState(false);
  const [imgDataUrl, setImgDataUrl]  = useState(null);
  const [popup, setPopup] = useState(null);
  const imgInputRef = useRef(null);

  // ── Field groups — pre-filled from initialValues when editing ──
  const [quick, setQuick] = useState({
    barcode:   initialValues?.code     || '',
    qty:       initialValues?.qty      || '1',
    rate:      initialValues?.rate     || '1',
    mrp:       initialValues?.mrp      || '2',
    salePrice: '',
  });
  const [cls, setCls] = useState({
    division: initialValues?.division || 'Textiles',
    dept:     initialValues?.dept     || 'Mens',
    category: initialValues?.category || 'MC',
    subcatg:  initialValues?.subcatg  || 'PSAREES',
    brand:    initialValues?.brand    || 'b',
    style:    initialValues?.style    || 'MYACCOUNTS',
    size:     initialValues?.size     || '-',
    color:    initialValues?.color    || 'c',
    design:   initialValues?.design   || 'd',
    cref:     initialValues?.cref     || '',
  });
  const [price, setPrice] = useState({
    qty:     initialValues?.qty    || '1',
    rate:    initialValues?.rate   || '1',
    wsp:     initialValues?.wsp    || '0',
    mrp:     initialValues?.mrp    || '2',
    discPer: initialValues?.disc   || '0',
    discChk: true, splMrp: '0', ownMrp: '0', listMrp: '0',
  });
  const [details, setDetails] = useState({
    hsnCode:     initialValues?.hsn      || '',
    articleCode: initialValues?.artCode  || '',
    articleName: initialValues?.name     || '',
    itemName:    initialValues?.name     || '',
    barcode:     initialValues?.code     || '',
    oemCode:     initialValues?.oemCode  || '',
    vendor:      initialValues?.vendor   || '',
    inputTax:    initialValues?.cgst     ? String(parseFloat(initialValues.cgst) * 2) : '5',
    outputTax:   initialValues?.sgst     ? String(parseFloat(initialValues.sgst) * 2) : '5',
    uom:         initialValues?.uom      || 'Qty',
    scanQty:     '1',
    expiryDt:    initialValues?.expiryDate || '',
  });
  const [others, setOthers] = useState({ freight: '0', octroi: '0', others: '0', tokens: '0' });

  const setQ = (k, v) => setQuick(p => ({ ...p, [k]: v }));
  const setC = (k, v) => setCls(p => ({ ...p, [k]: v }));
  const setP = (k, v) => setPrice(p => ({ ...p, [k]: v }));
  const setD = (k, v) => setDetails(p => ({ ...p, [k]: v }));
  const setO = (k, v) => setOthers(p => ({ ...p, [k]: v }));

  // ── Live markup / markdown ──
  const mrp = parseFloat(price.mrp) || 0;
  const rate = parseFloat(price.rate) || 0;
  const wsp  = parseFloat(price.wsp) || 0;
  const wspMarkdown = mrp > 0 ? ((mrp - wsp) / mrp * 100).toFixed(2) : '0.00';
  const mrpMarkup   = rate > 0 ? ((mrp - rate) / rate * 100).toFixed(2) : '0.00';

  // ── Image pick ──
  function handleImgChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => setImgDataUrl(ev.target.result);
    reader.readAsDataURL(f);
  }

  // ── Lkp select ──
  function handleLkpSelect(row, cols) {
    const dc = cols.find(c => c.displayMember) || cols[0];
    const dv = row[dc?.field || Object.keys(row)[0]] || '';
    if (popup?.cb) popup.cb(dv);
    setPopup(null);
  }

  // ── Save ──
  function handleSave() {
    const qtyN   = parseFloat(quick.qty) || 1;
    const rateN  = parseFloat(quick.rate) || 0;
    const mrpN   = parseFloat(quick.mrp) || 0;
    const discN  = parseFloat(price.discPer) || 0;
    const cgstN  = parseFloat(details.inputTax) || 0;
    const sgstN  = parseFloat(details.outputTax) || 0;
    const pr     = qtyN * rateN;
    const discA  = pr * discN / 100;
    const base   = pr - discA;
    const gstA   = base * (cgstN + sgstN) / 100;
    const amount = (gstType === 'incl' ? pr : base + gstA).toFixed(2);
    onSave({
      _id: Date.now() + Math.random(), gstType,
      code: quick.barcode, artCode: details.articleCode, oemCode: details.oemCode,
      name: details.itemName || '(new)', hsn: details.hsnCode,
      uom: details.uom || 'Qty',
      qty: quick.qty, rate: quick.rate, mrp: quick.mrp, price: pr.toFixed(2),
      wsp: price.wsp, disc: price.discPer, pn: 'P',
      cgst: details.inputTax, sgst: details.outputTax, amount,
      division: cls.division, dept: cls.dept, category: cls.category,
      subcatg: cls.subcatg, brand: cls.brand, style: cls.style,
      size: cls.size, color: cls.color, design: cls.design, cref: cls.cref,
      batchNo: '', mfgDate: '', expiryDate: details.expiryDt,
      smToken: others.tokens, commission: '0.00', scheme: '', remarks: '',
    });
    onClose();
  }

  const clsFields = [
    { k: 'division', lbl: 'Division', ak: 'Division' },
    { k: 'dept',     lbl: 'Dept',     ak: 'Dept' },
    { k: 'category', lbl: 'Category', ak: 'Category' },
    { k: 'subcatg',  lbl: 'Sub Catg', ak: 'Subcatg' },
    { k: 'brand',    lbl: 'Brand',    ak: 'Brand' },
    { k: 'style',    lbl: 'Style',    ak: 'Style' },
    { k: 'size',     lbl: 'Size',     ak: 'Size' },
    { k: 'color',    lbl: 'Color',    ak: 'Color' },
    { k: 'design',   lbl: 'Design',   ak: 'Design' },
    { k: 'cref',     lbl: 'C.REF',    ak: 'Cref' },
  ];

  return (
    <>
      <div className="pi2-overlay-bg" onClick={onClose} />
      <div className="pi2-modal-wrap">
        <div className="pi2-modal" style={{ width: 920, maxWidth: '98vw' }}>

          {/* Header */}
          <div className="pi2-modal-head">
            <div className="t">
              <small>{sourceScreen}</small>
              {isEdit ? 'Item Edit — Update Row Details (F3)' : 'Item Creation — New Item (F2)'}
            </div>
            <button className="x" onClick={onClose}>✕</button>
          </div>

          {/* Toast */}
          <div className="pi2-modal-toast">
            {isEdit
              ? 'ℹ️ Editing selected row. Click Update Details to save changes back to the grid.'
              : 'ℹ️ Item will inherit classification from the currently selected row, then save as a new master record.'
            }
          </div>

          {/* Quick entry */}
          <div className="pi2-quickentry" style={{ alignItems: 'flex-end' }}>
            {[
              ['barcode', 'Barcode / OEM Code', 'Scan or type…'],
              ['qty',     'Qty',                '1'],
              ['rate',    'Rate',               '1'],
              ['mrp',     'MRP',                '2'],
              ['salePrice','Sale Price',        '2'],
            ].map(([k, lbl, ph]) => (
              <div className="pi2-field" key={k}>
                <label>{lbl}</label>
                <input value={quick[k]} placeholder={ph} onChange={e => setQ(k, e.target.value)} />
              </div>
            ))}
            <div className="pi2-field">
              <label>&nbsp;</label>
              <button className="pi2-addfld" type="button" title="Generate one row per unit of Qty"
                onClick={() => setShowImeiMgr(true)}>
                🔢 IMEI / Serial (for Qty)
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="pi2-modal-body" style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12 }}>

            {/* Left: Image — opens ImageManagerModal */}
            <div>
              <div className="pi2-card">
                <div className="pi2-cardhead"><span className="pi2-dot" />Item Image</div>
                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  {(() => {
                    const itemKey = quick.barcode || details.barcode || 'new_item';
                    const imgs = loadImages(itemKey);
                    return imgs.length > 0
                      ? <img src={imgs[0].small} alt="item" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--pi2-border)' }} />
                      : <button type="button" style={{ width: 120, height: 120, border: '2px dashed var(--pi2-border)', borderRadius: 4, background: 'none', cursor: 'pointer', fontSize: 32, color: 'var(--pi2-ink-soft)' }}
                          onClick={() => setShowImgMgr(true)}>📷</button>;
                  })()}
                  <button className="pi2-addfld" type="button" onClick={() => setShowImgMgr(true)}>
                    🖼 Upload / Crop / Link
                  </button>
                  <span style={{ fontSize: 11, color: 'var(--pi2-ink-soft)' }}>
                    {loadImages(quick.barcode || details.barcode || 'new_item').length > 0 ? `${loadImages(quick.barcode || details.barcode || 'new_item').length} image(s) attached` : 'No image attached yet'}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: all other cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflow: 'auto', maxHeight: 520 }}>

              {/* Classification */}
              <div className="pi2-card">
                <div className="pi2-cardhead">
                  <span className="pi2-dot" />Select Group &amp; Categories
                  <button className="pi2-addfld" type="button" style={{ marginLeft: 'auto' }}>
                    <span>+</span>Add Field
                  </button>
                </div>
                <div className="pi2-fieldgrid pi2-modal-grid">
                  {clsFields.map(f => (
                    <div className="pi2-field" key={f.k}>
                      <label>{f.lbl}</label>
                      <AutoInput id={`nim-${f.k}`} value={cls[f.k]} dataKey={f.ak} onChange={v => setC(f.k, v)} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div className="pi2-card">
                <div className="pi2-cardhead">
                  <span className="pi2-dot" />Price
                  <div className="pi2-gsttoggle" style={{ display: 'flex', gap: 4, margin: '0 8px' }}>
                    <button type="button"
                      className={`pi2-gst-opt${gstType === 'excl' ? ' pi2-gst-excl-active' : ''}`}
                      onClick={() => setGstType('excl')}>GST Excl.</button>
                    <button type="button"
                      className={`pi2-gst-opt${gstType === 'incl' ? ' pi2-gst-incl-active' : ''}`}
                      onClick={() => setGstType('incl')}>GST Incl.</button>
                  </div>
                  <button className="pi2-addfld" type="button"><span>+</span>Add Field</button>
                </div>
                <div className="pi2-fieldgrid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                  {[
                    ['qty',    'Qty *',        false],
                    ['rate',   'Rate *',       false],
                    ['wsp',    'WSP',          false],
                    ['mrp',    'MRP *',        false],
                  ].map(([k, lbl]) => (
                    <div className="pi2-field" key={k}>
                      <label>{lbl}</label>
                      <input className="pi2-mono" value={price[k]} onChange={e => setP(k, e.target.value)} />
                    </div>
                  ))}
                  <div className="pi2-field">
                    <label><input type="checkbox" style={{ width: 12, height: 12, marginRight: 4 }}
                      checked={price.discChk} onChange={e => setP('discChk', e.target.checked)} />Disc Per</label>
                    <input className="pi2-mono" value={price.discPer} onChange={e => setP('discPer', e.target.value)} />
                  </div>
                  {[['splMrp','Spl. MRP'],['ownMrp','Own MRP'],['listMrp','List MRP']].map(([k, lbl]) => (
                    <div className="pi2-field" key={k}>
                      <label>{lbl}</label>
                      <input className="pi2-mono" value={price[k]} onChange={e => setP(k, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Item Details */}
              <div className="pi2-card">
                <div className="pi2-cardhead">
                  <span className="pi2-dot" />Item Details
                  <button className="pi2-addfld" type="button" style={{ marginLeft: 'auto' }}>
                    <span>+</span>Add Field
                  </button>
                </div>
                <div className="pi2-fieldgrid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                  <div className="pi2-field"><label>HSN Code</label>
                    <input value={details.hsnCode} readOnly onChange={e => setD('hsnCode', e.target.value)} style={{ background: 'var(--pi2-surface-alt, #f8f9fb)' }} /></div>
                  <div className="pi2-field"><label>Article Code</label>
                    <input value={details.articleCode} readOnly style={{ background: 'var(--pi2-surface-alt, #f8f9fb)' }} /></div>
                  <div className="pi2-field"><label>Article Name</label>
                    <input value={details.articleName} readOnly title={details.articleName} style={{ background: 'var(--pi2-surface-alt, #f8f9fb)' }} /></div>
                  <div className="pi2-field"><label>Item Name</label>
                    <input value={details.itemName} onChange={e => setD('itemName', e.target.value)} /></div>
                  <div className="pi2-field"><label>Barcode</label>
                    <input value={details.barcode} onChange={e => setD('barcode', e.target.value)} /></div>
                  <div className="pi2-field"><label>Manual Code / OEM Barcode *</label>
                    <input value={details.oemCode} onChange={e => setD('oemCode', e.target.value)} /></div>
                  <div className="pi2-field"><label>Vendor / Supplier</label>
                    <input value={details.vendor} onChange={e => setD('vendor', e.target.value)} /></div>
                  <div className="pi2-field"><label>Input Tax *</label>
                    <input className="pi2-mono" value={details.inputTax} onChange={e => setD('inputTax', e.target.value)} /></div>
                  <div className="pi2-field"><label>Output Tax *</label>
                    <input className="pi2-mono" value={details.outputTax} onChange={e => setD('outputTax', e.target.value)} /></div>
                  <div className="pi2-field"><label>UOM</label>
                    <input value={details.uom} onChange={e => setD('uom', e.target.value)} /></div>
                  <div className="pi2-field"><label>Scan Qty</label>
                    <input className="pi2-mono" value={details.scanQty} onChange={e => setD('scanQty', e.target.value)} /></div>
                  <div className="pi2-field"><label>Expiry Dt</label>
                    <input value={details.expiryDt} placeholder="DD-MM-YYYY" onChange={e => setD('expiryDt', e.target.value)} /></div>
                </div>
              </div>

              {/* Others */}
              <div className="pi2-card">
                <div className="pi2-cardhead">
                  <span className="pi2-dot" />Others
                  <button className="pi2-addfld" type="button" style={{ marginLeft: 'auto' }}>
                    <span>+</span>Add Field
                  </button>
                </div>
                <div className="pi2-fieldgrid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                  {[['freight','Freight'],['octroi','Octroi'],['others','Others'],['tokens','Tokens']].map(([k, lbl]) => (
                    <div className="pi2-field" key={k}>
                      <label>{lbl}</label>
                      <input className="pi2-mono" value={others[k]} onChange={e => setO(k, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Markup / Markdown badges */}
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ background: 'var(--pi2-tag-incl, #fff3cd)', border: '1px solid #ffc107', borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600, color: '#856404' }}>
                  WSP Markdown: {wspMarkdown}%
                </div>
                <div style={{ background: 'var(--pi2-tag-excl, #d1e7dd)', border: '1px solid #198754', borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600, color: '#0a3622' }}>
                  MRP Markup: {mrpMarkup}%
                </div>
              </div>

              {/* Inventory type + flags */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, padding: '4px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="nim-invtype" value="inventory"
                    checked={invType === 'inventory'} onChange={() => setInvType('inventory')} /> Inventory
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="nim-invtype" value="non-inventory"
                    checked={invType === 'non-inventory'} onChange={() => setInvType('non-inventory')} /> Non-Inventory
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={giftVoucher} onChange={e => setGiftVoucher(e.target.checked)} /> Gift Voucher
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginLeft: 'auto' }}>
                  <input type="checkbox" checked={reverseMrp} onChange={e => setReverseMrp(e.target.checked)} /> Reverse MRP Calculation
                </label>
              </div>

              {/* Legend */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: 'var(--pi2-ink-soft)', paddingBottom: 4 }}>
                <span style={{ cursor: 'pointer' }} onClick={() => setGstType('excl')}>
                  <span className="pi2-lg-sw pi2-lg-excl" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 4 }} />Exclusive
                </span>
                <span style={{ cursor: 'pointer' }} onClick={() => setGstType('incl')}>
                  <span className="pi2-lg-sw pi2-lg-incl" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 4 }} />Inclusive
                </span>
                <span>* Date should be DD-MM-YYYY</span>
              </div>

            </div>
          </div>

          {/* Footer */}
          <div className="pi2-modal-foot">
            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--pi2-ink-soft)' }}>
              <span><b>F1</b> Save Details</span>
              <span><b>F6</b> Focus Item Properties</span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="pi2-btn" type="button" onClick={onClose}>Cancel</button>
              <button className="pi2-btn primary" type="button" onClick={handleSave} data-testid="new-item-modal-save-btn">
                {isEdit ? 'Update Details' : 'Save Details'} <span className="pi2-kbd">F1</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* GetInfo popup for classification lookups */}
      {popup && (
        <GetInfoPopup screenId={popup.si.screenId} controlId={String(popup.si.controlId)}
          title="Select" onSelect={handleLkpSelect} onClose={() => setPopup(null)} />
      )}
      {/* Image Manager */}
      {showImgMgr && (
        <ImageManagerModal
          itemKey={quick.barcode || details.barcode || 'new_item'}
          itemLabel={details.itemName || 'New Item'}
          onSave={(ref) => { setShowImgMgr(false); }}
          onClose={() => setShowImgMgr(false)}
        />
      )}
      {/* IMEI / Serial Unit Identifier */}
      {showImeiMgr && (
        <UnitIdentifierModal
          row={{ code: quick.barcode || details.barcode || 'new_item', name: details.itemName || 'New Item', qty: quick.qty || '1' }}
          onSave={() => setShowImeiMgr(false)}
          onClose={() => setShowImeiMgr(false)}
        />
      )}
    </>
  );
}
